import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { max, map, compact, last, split, size, startCase, camelCase, entries, fromPairs, find, groupBy, filter } from 'lodash';
import { Section } from '.';
import { GENERAL_SETTINGS, UPDATE_GENERAL_SETTINGS, SET_CLIENT_STATE, PAGE_LIST, PAGE_DATA } from '../../../graphqls';

export default function ContentSettings() {
  const statuses = ['statusPending', 'onTrack', 'someDisruption', 'majorDisruption', 'upcoming', 'discontinued', 'completed'];
  const [showStatusBar, setShowStatusBar] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [displayLevel, setDisplayLevel] = useState(1);
  const [showIndicatorOnHeader, setShowIndicatorOnHeader] = useState(false);
  const [drilldownable, setDrilldownable] = useState(false);
  const [showLatestUpdate, setShowLatestUpdate] = useState(false);
  const [showMeasureDetail, setShowMeasureDetail] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showCustomFields, setShowCustomFields] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [displayClonedActions, setDisplayClonedActions] = useState('hide');
  const [displayLinkedProjects, setDisplayLinkedProjects] = useState('hide');
  const [displayLowestLevelDates, setDisplayLowestLevelDates] = useState(false);
  const [selectedStatus, setStatus] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedCustomFields, setSelectedCustomFields] = useState([]);
  const history = useHistory();
  const [setState] = useMutation(SET_CLIENT_STATE);
  const { stage } = window;
  const { loading: loadingGeneralSettings, data: { generalSettings = {} } = {} } = useQuery(GENERAL_SETTINGS, {
    onCompleted() {
      setShowStatusBar(generalSettings.showStatusBar);
      setShowProgress(generalSettings.showProgress);
      setDisplayLevel(generalSettings.displayLevel);
      setShowIndicatorOnHeader(generalSettings.showIndicatorOnHeader);
      setDrilldownable(generalSettings.drilldownable);
      setShowLatestUpdate(generalSettings.showLatestUpdate);
      setShowMeasureDetail(generalSettings.showMeasureDetail);
      setShowTags(generalSettings.showTags);
      setShowCustomFields(generalSettings.showCustomFields);
      setShowChecklist(generalSettings.showChecklist);
      setDisplayClonedActions(generalSettings.displayClonedActions);
      setDisplayLowestLevelDates(generalSettings?.displayLowestLevelDates);
      setDisplayLinkedProjects(generalSettings.displayLinkedProjects);
      setStatus(statuses.filter((status) => !generalSettings[camelCase(`show ${status.replace('some', 'minor')}`)]));
      setSelectedTags(generalSettings?.hideTags || []);
      setSelectedCustomFields(generalSettings?.hideCustomFields || []);
    },
  });
  const { loading: loadingPageList, data: { pageList = [] } = {} } = useQuery(PAGE_LIST);
  const [updateGeneralSettings] = useMutation(UPDATE_GENERAL_SETTINGS, {
    refetchQueries: [{ query: GENERAL_SETTINGS }, { query: PAGE_LIST }, { query: PAGE_DATA, variables: { name: 'home' } }],
    onCompleted() {
      history.push('/home/edit');
    },
  });
  const maxLevel =
    max(compact(map(pageList, 'numberWithLabel')).map((numberWithLabel) => size(split(last(split(numberWithLabel, ' ')), '.')))) || 1;

  if (loadingGeneralSettings || loadingPageList) {
    return '';
  }

  const handleSelectLevelOfDisplay = async ({ target: { value } }) => {
    let cascadeUpdater = {};

    if (
      {
        ONE: 1,
        TWO: 2,
        THREE: 3,
        FOUR: 4,
        FIVE: 5,
      }[value] > maxLevel
    ) {
      cascadeUpdater = { drilldownable: false };
      setDrilldownable(false);
    }

    await updateGeneralSettings({ variables: { ...cascadeUpdater, displayLevel: value }, refetchQueries: [{ query: PAGE_LIST }] });
    setDisplayLevel(value);
    history.push('/home/edit');
    window.location.reload();
  };
  const handleDrilldownable = async () => {
    await updateGeneralSettings({ variables: { drilldownable: !drilldownable }, refetchQueries: [{ query: PAGE_LIST }] });
    setDrilldownable(!drilldownable);
    window.location.reload();
  };
  const handleShowStatusBar = () => {
    let cascadeUpdater = {};

    if (showStatusBar) {
      cascadeUpdater = {
        showUpcoming: false,
        showDiscontinued: false,
        showStatusPending: false,
        showOnTrack: false,
        showCompleted: false,
        showMinorDisruption: false,
        showMajorDisruption: false,
      };

      setStatus(statuses);
    } else {
      cascadeUpdater = {
        showUpcoming: true,
        showDiscontinued: true,
        showStatusPending: true,
        showOnTrack: true,
        showCompleted: true,
        showMinorDisruption: true,
        showMajorDisruption: true,
      };

      setStatus([]);
    }

    updateGeneralSettings({ variables: { ...cascadeUpdater, showStatusBar: !showStatusBar } });
    setShowStatusBar(!showStatusBar);
  };
  const handleShowProgress = () => {
    updateGeneralSettings({ variables: { showProgress: !showProgress } });
    setShowProgress(!showProgress);
  };
  const handleShowIndicatorOnHeader = ({ target: { value } }) => {
    updateGeneralSettings({ variables: { showIndicatorOnHeader: JSON.parse(value) } });
    setShowIndicatorOnHeader(value);
  };
  const ToggleLatestUpdate = () => {
    updateGeneralSettings({ variables: { showLatestUpdate: !showLatestUpdate } });
    setShowLatestUpdate(!showLatestUpdate);
  };
  const ToggleChecklist = () => {
    updateGeneralSettings({ variables: { showChecklist: !showChecklist } });
    setShowChecklist(!showChecklist);
  };
  const ToggleMeasureDetail = () => {
    updateGeneralSettings({ variables: { showMeasureDetail: !showMeasureDetail } });
    setShowMeasureDetail(!showMeasureDetail);
  };
  const ToggleTags = () => {
    updateGeneralSettings({ variables: { showTags: !showTags } });
    setShowTags(!showTags);
  };
  const ToggleCustomFields = () => {
    updateGeneralSettings({ variables: { showCustomFields: !showCustomFields } });
    setShowCustomFields(!showCustomFields);
  };
  const switchDisplayClonedActions = ({ target: { value } }) => {
    updateGeneralSettings({ variables: { displayClonedActions: value } });
    setDisplayClonedActions(value);
  };
  const ToggleDisplayLowestLevelDates = () => {
    updateGeneralSettings({ variables: { displayLowestLevelDates: !displayLowestLevelDates } });
    setDisplayLowestLevelDates(!displayLowestLevelDates);
  };
  const switchDisplayLinkedProjects = ({ target: { value } }) => {
    updateGeneralSettings({ variables: { displayLinkedProjects: value } });
    setDisplayLinkedProjects(value);
  };
  const onSwitchStatus = (values) => {
    if (values && values.length) {
      const currentStatuses = map(values, 'value');

      setStatus(currentStatuses);
      updateGeneralSettings({
        variables: {
          ...fromPairs(
            statuses
              .map((status) => entries({ [camelCase(`show ${status.replace('some', 'minor')}`)]: !currentStatuses.includes(status) }))
              .flat(),
          ),
          showStatusBar: values.length !== 7,
        },
      });
      setShowStatusBar(values.length !== 7);
    } else {
      setStatus([]);
      updateGeneralSettings({
        variables: {
          ...fromPairs(statuses.map((status) => entries({ [camelCase(`show ${status.replace('some', 'minor')}`)]: true })).flat()),
          showStatusBar: true,
        },
      });
    }
  };
  const onSwitchTags = (values) => {
    if (values && values.length) {
      const currentTags = map(map(values, 'value'), Number);

      setSelectedTags(currentTags);
      updateGeneralSettings({ variables: { hideTags: currentTags } });
    } else {
      setSelectedTags([]);
      updateGeneralSettings({ variables: { hideTags: [] } });
    }
  };
  const onSwitchCustomFields = (values) => {
    if (values && values.length) {
      const currentCustomFields = map(map(values, 'value'), Number);

      setSelectedCustomFields(currentCustomFields);
      updateGeneralSettings({ variables: { hideCustomFields: currentCustomFields } });
    } else {
      setSelectedCustomFields([]);
      updateGeneralSettings({ variables: { hideCustomFields: [] } });
    }
  };
  const subPlanContentLabelsConfig = {
    label: 'Sub Plan Content Labels',
    list: {
      onClick: () => setState({ variables: { modalTypeShown: 'subPlanContentLabels' } }),
      dataTip: 'Edit',
      className: 'icon-btn',
    },
  };
  const displayStatusBarConfig = {
    label: 'Display Status Bar',
    toggle: { checked: showStatusBar || false, onChange: handleShowStatusBar },
  };
  const progressConfig = {
    label: 'Display Progress',
    toggle: { checked: showProgress || false, onChange: handleShowProgress },
  };
  const latestUpdateConfig = {
    label: 'Display Latest Updates',
    toggle: { checked: showLatestUpdate || false, onChange: ToggleLatestUpdate },
  };
  const measureDetailConfig = {
    label: 'Display Analytics Visual Comment',
    toggle: { checked: showMeasureDetail || false, onChange: ToggleMeasureDetail },
  };
  const tagConfig = { label: 'Display Tags', toggle: { checked: showTags || false, onChange: ToggleTags } };
  const customFieldConfig = {
    label: 'Display Custom Fields',
    toggle: { checked: showCustomFields || false, onChange: ToggleCustomFields },
  };
  const checklistConfig = {
    label: 'Display Lowest Level Checklist',
    toggle: { checked: showChecklist || false, onChange: ToggleChecklist },
  };
  const displayLowestLevelDatesConfig = {
    label: 'Display Lowest Level Start/End Dates',
    toggle: { checked: displayLowestLevelDates || false, onChange: ToggleDisplayLowestLevelDates },
  };
  const subPlanContentPositionConfig = {
    label: 'Display Sub Plan Content block below/above Measures on child pages',
    select: {
      className: 'number-of-columns',
      onChange: handleShowIndicatorOnHeader,
      value: showIndicatorOnHeader,
      options: [
        { label: 'Above', value: 'true' },
        { label: 'Below', value: 'false' },
      ],
    },
  };
  const levelConfig = {
    description: 'Choose the plan level to display plan updates, status, progress and measures',
    label: 'Display Plan Level',
    select: {
      className: 'number-of-columns',
      onChange: handleSelectLevelOfDisplay,
      value: displayLevel,
      options: [
        { label: 'Level 1', value: 'ONE' },
        { label: 'Level 1,2', value: 'TWO' },
        { label: 'Level 1,2,3', value: 'THREE' },
        { label: 'Level 1,2,3,4', value: 'FOUR' },
        { label: 'Level 1,2,3,4,5', value: 'FIVE' },
      ].slice(0, drilldownable ? maxLevel : maxLevel + 1),
    },
  };
  const drilldownConfig = {
    label: 'Enable Drill Down into the Details',
    toggle: {
      checked: drilldownable || false,
      onChange: handleDrilldownable,
      disabled: { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 }[displayLevel] > maxLevel,
    },
  };
  const cloneConfig = {
    label: 'Display Cloned Actions',
    select: {
      className: 'number-of-columns',
      onChange: switchDisplayClonedActions,
      value: displayClonedActions,
      options: [
        { label: 'Show cloned actions with rollup only', value: 'rollup' },
        { label: 'Show all cloned actions', value: 'all' },
        { label: 'Hide all cloned actions', value: 'hide' },
      ],
    },
  };
  const projectConfig = {
    label: 'Display Linked Projects',
    select: {
      className: 'number-of-columns',
      onChange: switchDisplayLinkedProjects,
      value: displayLinkedProjects,
      options: [
        { label: 'Show linked projects with roll up', value: 'rollup' },
        { label: 'Show linked projects', value: 'all' },
        { label: 'Hide linked projects', value: 'hide' },
      ],
    },
  };
  const statusConfig = {
    label: 'Hide Status Across Site',
    dropdown: {
      isClearable: false,
      isSearchable: false,
      isMulti: true,
      disabled: !showStatusBar,
      options: statuses.map((option) => ({ label: generalSettings[option] || startCase(option), value: option })),
      onChange: onSwitchStatus,
      value: selectedStatus.map((status) => ({ label: generalSettings[status] || startCase(status), value: status })),
      placeholder: 'All Status',
    },
  };
  const tagsConfig = {
    label: 'Hide Tags Across Site',
    dropdown: {
      isClearable: false,
      isSearchable: false,
      isMulti: true,
      disabled: !showTags,
      options: (generalSettings?.tags || []).map(({ tagId, tagName, colorCode }) => ({ label: tagName, value: tagId })),
      onChange: onSwitchTags,
      value: selectedTags.map((tagId) => ({ label: find(generalSettings?.tags || [], { tagId })?.tagName, value: tagId })),
      placeholder: 'All Tags',
    },
  };
  const customFieldsConfig = {
    label: 'Hide Custom Fields Across Site',
    dropdown: {
      isClearable: false,
      isSearchable: false,
      isMulti: true,
      disabled: !showCustomFields,
      options: map(
        entries(groupBy(generalSettings?.customFields || [], 'planningStructureId')),
        ([planningStructureId, structureFields], structureIndex) => ({
          label: `Level ${structureIndex + 1}: ${map(structureFields, 'fieldName').join(', ')}`,
          value: Number(planningStructureId),
        }),
      ),
      onChange: onSwitchCustomFields,
      value: compact(
        entries(groupBy(generalSettings?.customFields || [], 'planningStructureId')).map(([planningStructureId], structureIndex) => {
          return selectedCustomFields.includes(Number(planningStructureId))
            ? {
                label: `Level ${structureIndex + 1}: ${map(
                  filter(generalSettings?.customFields || [], { planningStructureId }),
                  'fieldName',
                ).join(', ')}`,
                value: Number(planningStructureId),
              }
            : null;
        }),
      ),
      placeholder: 'All CustomFields',
    },
  };

  return (
    <div className="general-settings content-setting">
      <strong className="general-setting-label">Content Settings</strong>
      <Section config={levelConfig} customClass="plan-level-section" />
      <Section config={drilldownConfig} />
      <Section config={subPlanContentLabelsConfig} customClass="sub-plan-content" />
      <Section config={subPlanContentPositionConfig} customClass="show-header-indicator" />
      <Section config={displayStatusBarConfig} />
      <Section config={statusConfig} customClass="status-section" />
      <Section config={progressConfig} />
      <Section config={displayLowestLevelDatesConfig} />
      <Section config={checklistConfig} />
      <Section config={latestUpdateConfig} />
      <Section config={measureDetailConfig} />
      <Section config={cloneConfig} />
      <Section config={projectConfig} />
      {!!generalSettings?.tags?.length && (
        <>
          <Section config={tagConfig} />
          {!!showTags && <Section config={tagsConfig} customClass="tag-section" />}
        </>
      )}
      {!!generalSettings?.customFields?.length && (
        <>
          <Section config={customFieldConfig} />
          {!!showCustomFields && <Section config={customFieldsConfig} customClass="custom-field-section" />}
        </>
      )}
    </div>
  );
}
